import { useState, useRef, useEffect } from 'react';
import { 
  Phone, 
  Camera, 
  Shield, 
  CheckCircle, 
  Loader2,
  AlertCircle,
  X,
  Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

export default function VerificationPanel({ onUpdate }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showSelfieModal, setShowSelfieModal] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/verify/status?token=${token}`);
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeColor = (level) => {
    if (level >= 2) return 'bg-gradient-to-r from-amber-500 to-yellow-500';
    if (level >= 1) return 'bg-gradient-to-r from-green-500 to-emerald-500';
    return 'bg-gray-400';
  };

  const getBadgeLabel = (level) => {
    if (level >= 2) return 'Gold Verified';
    if (level >= 1) return 'Phone Verified';
    return 'Not Verified';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-rose-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current Status */}
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Verification Status</h3>
          <div className={`px-3 py-1 rounded-full text-white text-sm font-medium ${getBadgeColor(status?.verification_level || 0)}`}>
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              {getBadgeLabel(status?.verification_level || 0)}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Verified profiles get 5x more responses. Complete verification to stand out!
        </p>

        {/* Verification Steps */}
        <div className="space-y-4">
          {/* Phone Verification */}
          <div className={`flex items-center justify-between p-4 rounded-xl ${
            status?.phone_verified ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                status?.phone_verified ? 'bg-green-100' : 'bg-gray-200'
              }`}>
                <Phone className={`w-5 h-5 ${status?.phone_verified ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-800">Phone Verification</p>
                <p className="text-sm text-gray-500">
                  {status?.phone_verified ? 'Verified' : 'Verify your phone number'}
                </p>
              </div>
            </div>
            {status?.phone_verified ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <button
                onClick={() => setShowPhoneModal(true)}
                className="px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 transition-colors"
              >
                Verify Now
              </button>
            )}
          </div>

          {/* Selfie Verification */}
          <div className={`flex items-center justify-between p-4 rounded-xl ${
            status?.selfie_verified ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                status?.selfie_verified ? 'bg-green-100' : 'bg-gray-200'
              }`}>
                <Camera className={`w-5 h-5 ${status?.selfie_verified ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-800">Selfie Verification</p>
                <p className="text-sm text-gray-500">
                  {status?.selfie_verified ? 'Verified' : 'Upload a selfie for verification'}
                </p>
              </div>
            </div>
            {status?.selfie_verified ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <button
                onClick={() => setShowSelfieModal(true)}
                className="px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 transition-colors"
              >
                Verify Now
              </button>
            )}
          </div>
        </div>

        {/* Fully Verified Banner */}
        {status?.is_fully_verified && (
          <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center">
                <Star className="w-6 h-6 text-white" fill="white" />
              </div>
              <div>
                <p className="font-bold text-amber-800">Gold Verified Member!</p>
                <p className="text-sm text-amber-600">Your profile is fully verified and trusted.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Phone Verification Modal */}
      {showPhoneModal && (
        <PhoneVerificationModal 
          onClose={() => setShowPhoneModal(false)}
          onSuccess={() => {
            setShowPhoneModal(false);
            fetchStatus();
            onUpdate?.();
          }}
        />
      )}

      {/* Selfie Verification Modal */}
      {showSelfieModal && (
        <SelfieVerificationModal 
          onClose={() => setShowSelfieModal(false)}
          onSuccess={() => {
            setShowSelfieModal(false);
            fetchStatus();
            onUpdate?.();
          }}
        />
      )}
    </div>
  );
}

function PhoneVerificationModal({ onClose, onSuccess }) {
  const [step, setStep] = useState('input'); // input, otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const sendOTP = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/verify/phone/send?phone_number=${phone}&token=${token}`);
      toast.success('OTP sent successfully!');
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/verify/phone/verify?otp=${otp}&token=${token}`);
      toast.success('Phone verified successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-800">Phone Verification</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'input' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="flex gap-2">
                <span className="px-4 py-3 bg-gray-100 rounded-lg text-gray-600">+91</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit number"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <button
              onClick={sendOTP}
              disabled={loading || phone.length < 10}
              className="w-full py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="send-otp-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
              Send OTP
            </button>

            <p className="text-xs text-gray-500 text-center">
              We'll send a 6-digit OTP to your phone number
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-gray-600">
                Enter the 6-digit OTP sent to
              </p>
              <p className="font-medium">+91 {phone}</p>
            </div>

            <div>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter OTP"
                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                data-testid="otp-input"
                maxLength={6}
              />
            </div>

            <button
              onClick={verifyOTP}
              disabled={loading || otp.length !== 6}
              className="w-full py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="verify-otp-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
              Verify OTP
            </button>

            <button
              onClick={() => setStep('input')}
              className="w-full py-2 text-gray-600 text-sm hover:text-gray-800"
            >
              Change phone number
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SelfieVerificationModal({ onClose, onSuccess }) {
  const [selfie, setSelfie] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB allowed.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPreview(event.target?.result);
      setSelfie(event.target?.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadSelfie = async () => {
    if (!selfie) {
      toast.error('Please select a selfie');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const formData = new FormData();
      formData.append('selfie_data', selfie);

      await axios.post(
        `${API}/verify/selfie/upload?token=${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Selfie verified successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload selfie');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-800">Selfie Verification</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-800">Selfie Guidelines:</p>
                <ul className="text-blue-700 mt-1 space-y-1">
                  <li>• Face should be clearly visible</li>
                  <li>• Good lighting, no filters</li>
                  <li>• Look directly at camera</li>
                  <li>• Similar to your profile photos</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center">
            {preview ? (
              <img src={preview} alt="Selfie preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-gray-400">
                <Camera className="w-16 h-16 mx-auto mb-2" />
                <p className="text-sm">Click to upload selfie</p>
              </div>
            )}
          </div>

          {/* Upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-600 font-medium rounded-lg hover:border-rose-400 hover:text-rose-500 transition-colors"
          >
            {preview ? 'Change Selfie' : 'Select Selfie'}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Submit button */}
          <button
            onClick={uploadSelfie}
            disabled={loading || !selfie}
            className="w-full py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-selfie-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
            Submit for Verification
          </button>
        </div>
      </div>
    </div>
  );
}
